import {format, parseISO} from 'date-fns';
import moment from 'moment';
import {IntlShape} from 'react-intl';
import {formatInTimeZone} from 'date-fns-tz';
import {editFieldLabel, formatEditSide, getDisplayReason} from '../../timesheets/audit-log/auditLogDisplay';
import {CellValue, ReportRow} from '../data/reportRowModel';
import {ReportGrain} from '../reportGrain';
import {humanizeToken} from '../tokenLabel';

/** A calendar day with no time on it. */
const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const DATE_FORMAT = 'd MMM yyyy';
const DATE_TIME_FORMAT = 'd MMM yyyy, HH:mm';

const A = 'timeRegAuditEvent';
export const AUDIT_PATHS = {
	timestamp: `${A}.timestamp`,
	workDate: `${A}.workDate`,
	weekStartDate: `${A}.weekStartDate`,
	timeType: `${A}.timeType`,
	reason: `${A}.reason`,
	approvalType: `${A}.approvalType`,
	fieldName: `${A}.field.name`,
	oldLabel: `${A}.field.oldLabel`,
	oldValue: `${A}.field.oldValue`,
	newLabel: `${A}.field.newLabel`,
	newValue: `${A}.field.newValue`,
	isMaterial: `${A}.field.isMaterial`,
} as const;

/** Same derivation as graphql's `TimeRegApprovalLogEntryType.isBypassEdit`: there is no column, only
 * the reason prefix. */
const BYPASS_PREFIX = 'BYPASS_EDIT: ';

export interface CellFormatterOptions {
	/** Reader locale and messages. Without it, labels fall back to readable tokens. */
	intl?: IntlShape;
	/** IANA zone to show times in. Defaults to the reader's browser zone; tests pin it. */
	timeZone?: string;
	/** Locale the zone NAME is spelled in. Defaults to the browser's `navigator.language`, not
	 * `intl.locale`: the app's IntlProvider locale is only ever en-US/en-GB/en-150/es/da/fr, and none
	 * of them names Sydney "AEST". Tests pin it. */
	zoneLocale?: string;
}

/** How the formatted value will be used. A group label may say more than a cell: Week grouping files
 * every row by the reader's calendar week, while a Week start cell states the week that was approved. */
export interface CellFormatContext {
	grouping?: boolean;
}

export type CellFormatter = (
	path: string,
	value: CellValue | undefined,
	row?: ReportRow,
	context?: CellFormatContext
) => CellValue | undefined;

/** The fields a formatter needs to know about, which is the subset of the taxonomy it reads. */
interface DisplayField {
	key: string;
	dataType?: string;
	values?: string[];
}

/** The zone's short name ("AEST", "GMT+10") at `date`. Takes a formatter built once per
 * `createCellFormatter`, not one per cell: constructing an `Intl.DateTimeFormat` is the expensive
 * part, and the table formats every visible timestamp on every render. */
function zoneLabel(date: Date, zoneNamer: Intl.DateTimeFormat): string {
	return zoneNamer.formatToParts(date).find(part => part.type === 'timeZoneName')?.value ?? '';
}

/** An ISO date stays a calendar day. An ISO instant becomes `d MMM yyyy, HH:mm <zone>` in `timeZone`,
 * with the zone named, so two readers in different zones can tell why their times differ. */
function formatDateString(value: string, timeZone: string, zoneNamer: Intl.DateTimeFormat): string {
	const trimmed = value.trim();
	if (trimmed === '') return value;
	try {
		const parsed = parseISO(trimmed);
		if (Number.isNaN(parsed.getTime())) return value;
		if (ISO_DATE_ONLY.test(trimmed)) return format(parsed, DATE_FORMAT);
		return `${formatInTimeZone(parsed, timeZone, DATE_TIME_FORMAT)} ${zoneLabel(parsed, zoneNamer)}`.trim();
	} catch {
		return value;
	}
}

const textOf = (row: ReportRow | undefined, path: string): string | null => {
	const cell = row?.cells[path];
	return cell && (cell.kind === 'text' || cell.kind === 'date') && cell.value !== '' ? cell.value : null;
};

/** Leaves a cell exactly as the mapper produced it. Used where no taxonomy has resolved yet. */
export const identityCellFormatter: CellFormatter = (_path, value) => value;

/**
 * Formats cells for display, driven by what the grain's taxonomy says each field is.
 *
 * Two things were reaching the screen raw. A field with a fixed vocabulary rendered its wire token
 * (`STATUS_CHANGE`), and a date-typed field rendered its ISO string (`2026-08-27T09:12:00Z`) —
 * both correct as data and both unreadable as a column. The taxonomy already says which fields are
 * which, so this reads it rather than keeping a second list in step by hand.
 *
 * Formatting happens at render only. Sorting and filtering keep working on the underlying value, so
 * a column still orders by its real date rather than alphabetically by its formatted label.
 */
/** The grains whose cells this formats. Deliberately not every grain: the task and project-status
 * columns that publish a vocabulary already have their own wording - RAG and Stage go through
 * `formatLabel` in the mapper, and Budget type renders the raw wire token ON PURPOSE, so that the
 * value in the column and the value in the condition picker read the same. Formatting those here
 * would break that agreement and restate two label maps that already exist. The audit grain has no
 * such maps, and its tokens and timestamps are what this exists for. */
const FORMATTED_GRAINS: ReadonlySet<ReportGrain> = new Set<ReportGrain>(['TIME_REG_AUDIT_LOG']);

export function createCellFormatter(
	taxonomy: {fields?: DisplayField[]} | null | undefined,
	grain: ReportGrain | null,
	options: CellFormatterOptions = {}
): CellFormatter {
	// Required, not optional. While it could be omitted the default was "format everything", so a
	// caller who simply did not pass it would silently reformat the Budget type and RAG tokens the
	// comment above says must stay raw. There is no live bug -- the one caller passes `rowGrainOf()`
	// -- and making it required is what keeps that true.
	if (grain === null || !FORMATTED_GRAINS.has(grain)) {
		return identityCellFormatter;
	}
	const vocabularyPaths = new Set<string>();
	const datePaths = new Set<string>();

	for (const field of taxonomy?.fields ?? []) {
		if (field.values && field.values.length > 0) {
			vocabularyPaths.add(field.key);
		}
		if (field.dataType === 'date') {
			datePaths.add(field.key);
		}
	}

	if (vocabularyPaths.size === 0 && datePaths.size === 0) {
		return identityCellFormatter;
	}

	const timeZone = options.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
	const zoneLocale = options.zoneLocale ?? (typeof navigator !== 'undefined' ? navigator.language : undefined) ?? 'en';
	const zoneNamer = new Intl.DateTimeFormat(zoneLocale, {timeZone, timeZoneName: 'short'});
	const intl = options.intl;
	const date = (v: string): string => formatDateString(v, timeZone, zoneNamer);
	const weekOf = (isoDay: string): CellValue => {
		const day = date(isoDay);
		return {
			kind: 'text',
			value: intl ? intl.formatMessage({id: 'ai_report_builder.audit.week_of'}, {date: day}) : `Week of ${day}`,
		};
	};

	return (path, value, row, context) => {
		// Values computed from the row come first: a week-approval row has no work date of its own,
		// and Before/After read their sibling value, so `value` alone cannot decide them.
		if (path === AUDIT_PATHS.workDate && textOf(row, AUDIT_PATHS.timeType) === 'WEEK_APPROVAL') {
			const week = textOf(row, AUDIT_PATHS.weekStartDate);
			return week ? weekOf(week) : undefined;
		}
		if (path === AUDIT_PATHS.oldLabel || path === AUDIT_PATHS.newLabel || path === AUDIT_PATHS.isMaterial) {
			const fieldName = textOf(row, AUDIT_PATHS.fieldName);
			if (!fieldName) return undefined; // not an edit row: nothing before, after, or material
			if (path === AUDIT_PATHS.isMaterial) return value;
			const old = path === AUDIT_PATHS.oldLabel;
			const side = {
				fieldName,
				label: textOf(row, old ? AUDIT_PATHS.oldLabel : AUDIT_PATHS.newLabel),
				value: textOf(row, old ? AUDIT_PATHS.oldValue : AUDIT_PATHS.newValue),
			};
			return {kind: 'text', value: intl ? formatEditSide(side, intl) : side.label ?? side.value ?? ''};
		}
		if (path === AUDIT_PATHS.weekStartDate && context?.grouping && value?.kind !== 'date') {
			// Grouping only. Only week-approval rows carry a week start; for Week grouping a registration
			// row's week is the one its work date falls in, by the same locale week the timesheet screens
			// build (`startOf('week')`), so it is filed where the reader would find it in timesheets. A
			// Week start CELL on a registration row stays empty: there is no stored week to show.
			const workDate = textOf(row, AUDIT_PATHS.workDate);
			return workDate ? weekOf(moment(workDate, 'YYYY-MM-DD').startOf('week').format('YYYY-MM-DD')) : value;
		}
		if (!value) return value;
		if (path === AUDIT_PATHS.reason && value.kind === 'text') {
			// The drawer's own call, with isBypassEdit derived the way graphql derives it, so the
			// report and the drawer cannot describe one event two ways (a bypass breadcrumb is hidden
			// in both). No report-only wording: any new copy would need a translation key.
			const shown = getDisplayReason(value.value, value.value.startsWith(BYPASS_PREFIX))?.trim();
			return shown ? {kind: 'text', value: shown} : undefined;
		}
		if (path === AUDIT_PATHS.approvalType && value.kind === 'text') {
			return {kind: 'text', value: humanizeToken(value.value === 'UNAPPROVE' ? 'REVERSE_APPROVAL' : value.value)};
		}
		if (path === AUDIT_PATHS.fieldName && value.kind === 'text') {
			return {kind: 'text', value: intl ? editFieldLabel(value.value, intl) : humanizeToken(value.value)};
		}
		if (path === AUDIT_PATHS.weekStartDate && value.kind === 'date') {
			// A cell states the week that was approved, as Work date does on the same row. Only a group label
			// is snapped: a stored week start follows the APPROVER's locale (Sunday or Monday), while
			// registration rows above follow the reader's, so the group uses the reader's week sharing most
			// of the stored week's days (four of seven, hence +3) to keep one calendar week in one group.
			return context?.grouping
				? weekOf(moment(value.value, 'YYYY-MM-DD').add(3, 'days').startOf('week').format('YYYY-MM-DD'))
				: weekOf(value.value);
		}
		if (value.kind === 'date') return {kind: 'date', value: date(value.value)};
		if (value.kind !== 'text') return value;
		if (vocabularyPaths.has(path)) return {kind: 'text', value: humanizeToken(value.value)};
		if (datePaths.has(path)) return {kind: 'text', value: date(value.value)};
		return value;
	};
}
